import { useEffect, useRef } from 'react';
import { REFERENCE_LAYERS_MIN_ZOOM, ZONAS_INUNDABLES_LAYER_STYLE } from '../styles/mapTheme';
import {
  getSignedPmtilesUrl,
  refreshPmtilesVectorSourceIfNeeded,
} from '../../Utils/pmtilesAuth';
import { waitForMapStyleReady } from '../utils/waitForMapStyleReady';

// ─── Constantes ──────────────────────────────────────────────────────────────

const SOURCE_ID = 'zonas-inundables';
const LAYER_FILL_ID = 'zonas-inundables-fill';
const LAYER_OUTLINE_ID = 'zonas-inundables-outline';
const SOURCE_LAYER = 'zonas_inundables';

/**
 * URL original del PMTiles en S3 (privado).
 */
const PMTILES_ORIGINAL_URL =
  'pmtiles://https://s3.us-east-1.amazonaws.com/argenlot.lots/tiles/reference/zonas_inundables_v1.pmtiles';

const MIN_ZOOM = REFERENCE_LAYERS_MIN_ZOOM.zonasInundables;
const MAX_ZOOM = 20;

const LAYER_IDS = [LAYER_FILL_ID, LAYER_OUTLINE_ID];

const { fillOpacity, outlineColor, outlineWidth, outlineOpacity, incidenciaColors } =
  ZONAS_INUNDABLES_LAYER_STYLE;

const FILL_COLOR_EXPRESSION = [
  'match',
  ['get', 'incidencia'],
  1, incidenciaColors[1],
  2, incidenciaColors[2],
  3, incidenciaColors[3],
  incidenciaColors.fallback,
];


function _getInsertBeforeLayer(map) {
  const layers = map.getStyle()?.layers ?? [];
  const parcelLayer = layers.find((l) => l.id.startsWith('self-hosted-vector-layer-'));
  return parcelLayer?.id ?? undefined;
}

function applyVisibility(map, visible) {
  const vis = visible ? 'visible' : 'none';
  LAYER_IDS.forEach((layerId) => {
    if (map.getLayer(layerId)) {
      map.setLayoutProperty(layerId, 'visibility', vis);
    }
  });
}

function ensureSourceAndLayers(map, signedUrl, visible) {
  const visibility = visible ? 'visible' : 'none';

  if (!map.getSource(SOURCE_ID)) {
    map.addSource(SOURCE_ID, {
      type: 'vector',
      url: signedUrl,
    });
  }

  if (!map.getLayer(LAYER_FILL_ID)) {
    map.addLayer(
      {
        id: LAYER_FILL_ID,
        type: 'fill',
        source: SOURCE_ID,
        'source-layer': SOURCE_LAYER,
        minzoom: MIN_ZOOM,
        maxzoom: MAX_ZOOM,
        layout: { visibility },
        paint: {
          'fill-color': FILL_COLOR_EXPRESSION,
          'fill-opacity': fillOpacity,
        },
      },
      _getInsertBeforeLayer(map),
    );
  }

  if (!map.getLayer(LAYER_OUTLINE_ID)) {
    map.addLayer(
      {
        id: LAYER_OUTLINE_ID,
        type: 'line',
        source: SOURCE_ID,
        'source-layer': SOURCE_LAYER,
        minzoom: MIN_ZOOM,
        maxzoom: MAX_ZOOM,
        layout: { visibility },
        paint: {
          'line-color': outlineColor,
          'line-width': outlineWidth,
          'line-opacity': outlineOpacity,
        },
      },
      _getInsertBeforeLayer(map),
    );
  }

  applyVisibility(map, visible);
}

function detachMoveEnd(map, moveEndHandlerRef) {
  const handler = moveEndHandlerRef.current;
  if (handler && map) map.off('moveend', handler);
  moveEndHandlerRef.current = null;
}

function attachMoveEnd(map, moveEndHandlerRef, enabledRef) {
  detachMoveEnd(map, moveEndHandlerRef);

  const onMoveEnd = () => {
    if (!enabledRef.current) return;

    void refreshPmtilesVectorSourceIfNeeded(map, SOURCE_ID, PMTILES_ORIGINAL_URL)
      .finally(() => {
        applyVisibility(map, enabledRef.current);
      });
  };

  moveEndHandlerRef.current = onMoveEnd;
  map.on('moveend', onMoveEnd);
}

// ─── Hook ────────────────────────────────────────────────────────────────────

/**
 * Gestiona la capa de zonas inundables sobre MapLibre.
 *
 * @param {object}                 options
 * @param {React.MutableRefObject} options.mapInstanceRef  Ref al mapa MapLibre.
 * @param {boolean}                [options.enabled=false] Mostrar la capa.
 */
export function useZonasInundablesLayer({ mapInstanceRef, enabled = false, mapAvailTick = 0 }) {
  const enabledRef = useRef(enabled);
  const layersReadyRef = useRef(false);
  const moveEndHandlerRef = useRef(null);
  const setupGenRef = useRef(0);

  enabledRef.current = enabled;

  // Ocultar/mostrar de inmediato cuando cambia el toggle (sin esperar async).
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map || !layersReadyRef.current) return;
    applyVisibility(map, enabled);
    if (!enabled) detachMoveEnd(map, moveEndHandlerRef);
  }, [enabled, mapInstanceRef]);

  // Cargar source/capas solo cuando se activa; renovar firma solo mientras esté activa.
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map) return undefined;

    if (!enabled) {
      applyVisibility(map, false);
      detachMoveEnd(map, moveEndHandlerRef);
      layersReadyRef.current = false;
      return undefined;
    }

    const setupGen = setupGenRef.current + 1;
    setupGenRef.current = setupGen;
    let cancelled = false;

    const setup = async () => {
      await waitForMapStyleReady(map);
      if (cancelled || setupGenRef.current !== setupGen || !enabledRef.current) return;

      const signedUrl = await getSignedPmtilesUrl(PMTILES_ORIGINAL_URL);
      if (cancelled || setupGenRef.current !== setupGen || !enabledRef.current) return;
      if (!signedUrl) return;

      ensureSourceAndLayers(map, signedUrl, true);
      layersReadyRef.current = true;
      applyVisibility(map, true);
      attachMoveEnd(map, moveEndHandlerRef, enabledRef);
    };

    void setup();

    return () => {
      cancelled = true;
      detachMoveEnd(map, moveEndHandlerRef);
    };
  }, [enabled, mapInstanceRef, mapAvailTick]);
}
